/** @jsx jsx */
import { useFocusRing } from '@react-aria/focus';
import { AriaListBoxOptions, useListBox, useOption } from '@react-aria/listbox';
import { mergeProps } from '@react-aria/utils';
import { ListState } from '@react-stately/list';
import { jsx } from '@theme-ui/core';
import * as React from 'react';
import { Icon } from './Icon2';
import type { Node } from '@react-types/shared';
import { Text } from './Text';
import { Inline } from './Inline';
import { Input } from './../../packages/base-elements/Input';
import { useTranslation } from '@front10/utils';

interface ListBoxProps extends AriaListBoxOptions<unknown> {
  listBoxRef?: React.RefObject<HTMLUListElement>;
  state: any;
  showSearchOptions?: boolean;
  selectAllOptions?: {
    label: string;
    value: string;
  };
  noResultLabel?: string;
  isSelectedAllOptions?: boolean;
  setIsSelectedAllOptions?: (value: boolean) => void;
}

const ENTER_KEY_CODE = 13;
const SPACE_KEY_CODE = 32;
const DOWN_ARROW_KEY_CODE = 40;
const UP_ARROW_KEY_CODE = 38;

export function ListBox(props: ListBoxProps) {
  // Get props for the listbox element
  let ref = React.useRef(null);
  const {
    state,
    showSearchOptions,
    selectAllOptions,
    isSelectedAllOptions,
    setIsSelectedAllOptions,
    noResultLabel,
    listBoxRef = ref,
    ...restProps
  } = props;

  const translate = useTranslation();
  let { listBoxProps } = useListBox(restProps, state, listBoxRef);

  const isMultiple = state.selectionManager.selectionMode === 'multiple';

  React.useEffect(() => {
    if (setIsSelectedAllOptions) {
      setIsSelectedAllOptions(state.selectionManager.isSelectAll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectionManager.isSelectAll]);

  const Option = isMultiple ? OptionMultiple : OptionSingle;

  const [inputValueSearch, setInputValueSearch] = React.useState('');
  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValueSearch = event.target.value;
    setInputValueSearch(inputValueSearch);
    setExistFilteredAvailableOptions(null);
  };

  const [existInitialAvailableOptions, setExistInitialAvailableOptions] = React.useState<
    null | boolean
  >(null);
  const [existFilteredAvailableOptions, setExistFilteredAvailableOptions] = React.useState<
    null | boolean
  >(null);

  const getAllOptions = () => {
    const collection = [...state.collection];
    if (collection.length === 0) {
      if (existInitialAvailableOptions === null) {
        setExistInitialAvailableOptions(false);
      }
    }
    return (
      <React.Fragment>
        {collection.map((item, index) => (
          <Option
            key={item.key}
            item={item}
            state={state}
            isFirstOption={index === 0}
            listBoxRef={listBoxRef}
          />
        ))}
      </React.Fragment>
    );
  };

  const getFilteredOptions = () => {
    const filteredOptions = [...state.collection].filter((item) =>
      item.textValue.toUpperCase().includes(inputValueSearch.toLocaleUpperCase())
    );
    if (filteredOptions.length === 0) {
      if (existFilteredAvailableOptions === null) {
        setExistFilteredAvailableOptions(false);
      }
    }

    return (
      <React.Fragment>
        {Boolean(inputValueSearch)
          ? filteredOptions.map((item, index) => {
              return (
                <Option
                  key={item.key}
                  item={item}
                  state={state}
                  isFirstOption={index === 0}
                  listBoxRef={listBoxRef}
                />
              );
            })
          : getAllOptions()}
      </React.Fragment>
    );
  };

  const noResult = (
    <Inline variant="noResultInfo.container">
      <Text as="span" variant="noResultInfo.text">
        {translate(noResultLabel ?? '', 'No results to show')}
      </Text>
    </Inline>
  );

  const getOverflowStyle = () => {
    const totalOfItems = [...state.collection].length;
    const overFlowStyle = { overflow: 'auto', 'overflow-x': 'hidden' };
    if (showSearchOptions && selectAllOptions) {
      if (!Boolean(inputValueSearch)) {
        if (existInitialAvailableOptions !== false && existFilteredAvailableOptions !== false) {
          return totalOfItems + 1 > 4 ? overFlowStyle : {};
        }
      } else {
        const filteredOptions = [...state.collection].filter((item) =>
          item.textValue.toUpperCase().includes(inputValueSearch.toLocaleUpperCase())
        );
        return filteredOptions.length > 4 ? overFlowStyle : {};
      }
    }
    return totalOfItems > 4 ? overFlowStyle : {};
  };

  return (
    <React.Fragment>
      {showSearchOptions && selectAllOptions && existInitialAvailableOptions !== false && (
        <Inline variant="searchInput.container">
          <Input
            placeholder={translate('cmp-filter-search-placeholder-label', 'Search')}
            value={inputValueSearch}
            onChange={handleSearchInput}
            variant="searchInput.input"
          />
        </Inline>
      )}
      {(existInitialAvailableOptions === false || existFilteredAvailableOptions === false) && (
        <React.Fragment>{noResult}</React.Fragment>
      )}
      <ul
        {...listBoxProps}
        ref={listBoxRef}
        sx={{
          margin: 0,
          py: isMultiple ? 3 : 1,
          px: isMultiple ? '10px' : 0,
          listStyle: 'none',
          variant: 'layout.listbox.ul',
          ...getOverflowStyle()
        }}
        tabIndex={showSearchOptions && selectAllOptions ? -1 : listBoxProps.tabIndex}
      >
        {showSearchOptions &&
          selectAllOptions &&
          !Boolean(inputValueSearch) &&
          existInitialAvailableOptions !== false &&
          existFilteredAvailableOptions !== false && (
            <OptionSelectAll
              key={selectAllOptions.value}
              item={{ value: selectAllOptions.value, label: selectAllOptions.label }}
              state={state}
              isSelectedAllOptions={isSelectedAllOptions}
              setIsSelectedAllOptions={setIsSelectedAllOptions}
              listBoxRef={listBoxRef}
            />
          )}
        {!showSearchOptions ? getAllOptions() : getFilteredOptions()}
      </ul>
    </React.Fragment>
  );
}

interface OptionProps {
  item: Node<unknown>;
  state: ListState<unknown>;
  isFirstOption?: boolean;
  listBoxRef?: any;
}

interface OptionSelectAllProps {
  item: {
    label: string;
    value: string;
  };
  state: ListState<unknown>;
  isSelectedAllOptions?: boolean;
  setIsSelectedAllOptions?: (value: boolean) => void;
  listBoxRef?: any;
}

function OptionSelectAll({
  item,
  state,
  isSelectedAllOptions,
  setIsSelectedAllOptions,
  listBoxRef
}: OptionSelectAllProps) {
  // Get props for the option element
  let ref = React.useRef<any>(null);
  const [isFocus, setIsFocus] = React.useState(false);
  const translate = useTranslation();

  let { optionProps } = useOption({ key: item.value }, state, ref);

  // Determine whether we should show a keyboard
  // focus ring for accessibility
  let { isFocusVisible, focusProps } = useFocusRing();

  const inlineProps = mergeProps(optionProps, focusProps);
  const { id } = inlineProps;

  const handleOnClick = () => {
    if (isSelectedAllOptions) {
      state.selectionManager.clearSelection();
    } else {
      state.selectionManager.selectAll();
    }
    if (setIsSelectedAllOptions) {
      setIsSelectedAllOptions(!isSelectedAllOptions);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.keyCode === ENTER_KEY_CODE || e.keyCode === SPACE_KEY_CODE) {
      e.preventDefault();
      handleOnClick();
    } else if (e.keyCode === DOWN_ARROW_KEY_CODE) {
      e.preventDefault();
      if (listBoxRef?.current) {
        const listLi = listBoxRef?.current?.querySelectorAll('li');
        const firstLi = listLi[0];
        if (firstLi) {
          firstLi?.focus();
        }
      }
    } else if (e.keyCode === UP_ARROW_KEY_CODE) {
      e.preventDefault();
    }
  };

  return (
    <React.Fragment>
      <Inline
        tabIndex={0}
        role="option"
        id={id}
        onFocus={(e) => {
          setIsFocus(true);
          if (inlineProps?.onFocus) {
            inlineProps?.onFocus(e);
          }
        }}
        onBlur={() => {
          setIsFocus(false);
        }}
        data-item-highlighted={isFocus ? isFocus : undefined}
        onKeyDown={handleKeyDown}
        onClick={handleOnClick}
        ref={ref}
        as="li"
        gap="2"
        alignY="center"
        variant="menuItems.selectAllItem"
      >
        <Icon
          icon={isSelectedAllOptions ? 'checkbox' : 'checkbox-outline'}
          focusable={isFocusVisible}
          size={24}
          aria-hidden="true"
          sx={{ variant: 'menuItems.multi.checkbox' }}
        />
        <Text as="span" variant="fontSize5">
          {translate(item.label, item.label)}
        </Text>
      </Inline>
      <Inline variant="menuItems.selectAllItemBar"></Inline>
    </React.Fragment>
  );
}

function OptionMultiple({ item, state, isFirstOption = false, listBoxRef }: OptionProps) {
  // Get props for the option element
  let ref = React.useRef<any>(null);
  let { optionProps, isSelected } = useOption({ key: item.key }, state, ref);

  const translate = useTranslation();
  // Determine whether we should show a keyboard
  // focus ring for accessibility
  let { isFocusVisible, focusProps } = useFocusRing();
  const newMergeProps = { ...mergeProps(optionProps, focusProps) };
  return (
    <Inline
      {...newMergeProps}
      ref={ref}
      as="li"
      gap="2"
      alignY="center"
      data-item-highlighted={isFocusVisible || undefined}
      variant="menuItems.multi"
      onKeyDown={(e) => {
        if (isFirstOption) {
          if (e.keyCode === UP_ARROW_KEY_CODE) {
            if (listBoxRef?.current) {
              const listLi = listBoxRef?.current?.querySelectorAll('li');
              const firstLi = listLi[0];
              if (firstLi) {
                firstLi?.focus();
              }
            }
          }
        }
        if (newMergeProps?.onKeyDown) {
          newMergeProps?.onKeyDown(e);
        }
      }}
    >
      <Icon
        icon={isSelected ? 'checkbox' : 'checkbox-outline'}
        focusable={isFocusVisible}
        size={24}
        aria-hidden="true"
        sx={{ variant: 'menuItems.multi.checkbox' }}
      />
      <Text as="span" variant="fontSize5">
        {translate(item.textValue, item.textValue)}
      </Text>
    </Inline>
  );
}

function OptionSingle({ item, state }: OptionProps) {
  // Get props for the option element
  let ref = React.useRef(null);
  let { optionProps, isSelected } = useOption({ key: item.key }, state, ref);

  const translate = useTranslation();
  // Determine whether we should show a keyboard
  // focus ring for accessibility
  let { isFocusVisible, focusProps } = useFocusRing();
  let color: string = null!;

  return (
    <li
      {...mergeProps(optionProps, focusProps)}
      ref={ref}
      style={{
        color
      }}
      data-item-highlighted={isFocusVisible || undefined}
      sx={{
        variant: 'menuItems.default'
      }}
    >
      {translate(item.textValue, item.textValue)}
      {isSelected ? (
        <Icon aria-hidden="true" icon="check" size="1em" />
      ) : (
        <span aria-hidden="true" sx={{ display: 'block', width: '1em' }} />
      )}
    </li>
  );
}
